import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useSearchParams } from 'react-router-dom';
import { useInstanceId } from '../contexts/InstanceContext';
import { useTranslation } from '../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../config/api';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';

// specs/rag-source-heatmap.md — v2. A fresh module from v1
// (RagSourceMapScreen.jsx) per CLAUDE.md's redesign rule: the shape
// genuinely diverges (two independently-laid-out panels, a deterministic
// server-computed layout instead of a live d3-force simulation, a dataset
// switcher, an inventory/no-usage-data toggle). v1 stays live, unedited, at
// /rag-source-map.
//
// Layout (x/y/r for every circle, every cluster's center+radius+label
// anchor) is fully precomputed server-side (apps/backend/src/utils/ragSourceMapV2Layout.js)
// and deterministic — this component only ever draws given numbers via
// plain declarative SVG. No d3, no simulation, no client-side pan/zoom (out
// of scope for this pass — see PR notes): each panel's <svg viewBox> just
// fits its own precomputed extent, so the whole corpus is visible at once,
// which suits "must read as browsable" better than requiring a zoom gesture
// to discover it anyway.

const MONO_FONT = "'JetBrains Mono', 'SF Mono', monospace";
const REFRESH_INTERVAL_MS = 20000;
const HIT_RADIUS = 22; // spec: invisible hit area >= 22px radius, so a small unused source is as easy to click as a large used one
const PANEL_MARGIN = 44;

function contextLabel(t, context) {
  const key = `ragSourceMap.contexts.${context}`;
  const translated = t(key);
  if (translated == null || translated === key) {
    return context.charAt(0).toUpperCase() + context.slice(1).replace(/_/g, ' ');
  }
  return translated;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: radial-gradient(ellipse at 50% 40%, #14141f 0%, #0a0a12 60%, #050508 100%);
  color: white;
  position: relative;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  z-index: 10;
`;

const Title = styled.h1`
  color: rgba(255, 255, 255, 0.92);
  font-family: ${MONO_FONT};
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  margin: 0;
`;

const DefaultViewToggle = styled.button`
  font-family: ${MONO_FONT};
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.25)')};
  background: ${props => (props.$active ? 'rgba(250,204,21,0.15)' : 'transparent')};
  color: ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.7)')};

  &:hover {
    border-color: ${props => (props.$active ? '#FACC15' : 'rgba(255,255,255,0.5)')};
  }
`;

const SplitCanvas = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  & + & {
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const PanelHeader = styled.div`
  padding: 14px 20px 8px;
  z-index: 5;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PanelTitle = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const PanelAxisLabel = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
  margin-top: 2px;
`;

const PanelCounter = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 4px;
`;

const TarkistettavaNote = styled.div`
  font-family: ${MONO_FONT};
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
`;

const Switcher = styled.select`
  font-family: ${MONO_FONT};
  font-size: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 3px 8px;
  cursor: pointer;
`;

const SvgWrap = styled.div`
  flex: 1;
  min-height: 0;
`;

const Legend = styled.div`
  position: absolute;
  bottom: 16px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: ${MONO_FONT};
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(10, 10, 16, 0.5);
  border-radius: 8px;
  padding: 8px 10px;
  z-index: 5;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendSwatch = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${props => props.color};
  display: inline-block;
`;

const StatusMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.55);
  font-family: ${MONO_FONT};
  font-size: 1rem;
  text-align: center;
`;

const DetailPanel = styled.div`
  position: absolute;
  top: 70px;
  right: 24px;
  width: 320px;
  max-width: calc(100% - 48px);
  max-height: calc(100% - 100px);
  overflow-y: auto;
  background: rgba(14, 14, 22, 0.88);
  border: 1px solid ${props => props.accent}55;
  border-radius: 10px;
  padding: 20px;
  color: white;
  font-family: ${MONO_FONT};
  backdrop-filter: blur(14px);
  z-index: 20;
  box-shadow: 0 0 30px ${props => props.accent}22;
`;

const DetailTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 8px;
  padding-right: 20px;
  line-height: 1.35;
`;

const DetailType = styled.div`
  font-size: 0.75rem;
  color: ${props => props.accent};
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailMeta = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
`;

const DetailDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;

  &:hover { color: white; }
`;

const NotRetrievedBadge = styled.div`
  display: inline-block;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  padding: 1px 8px;
  margin: -6px 0 16px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailTotal = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  margin-bottom: 2px;
`;

const DetailTotalLabel = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

// One panel = one <svg>, own viewBox fit to its own precomputed extent.
function Panel({ panelId, panelData, title, labelNamespace, useDefaultView, switcher, selected, hovered, onSelect, onHover, t }) {
  const layout = useDefaultView ? panelData.defaultLayout : panelData.usageLayout;
  const half = layout.radius + PANEL_MARGIN;
  const viewBox = `${-half} ${-half} ${half * 2} ${half * 2}`;
  const sizeLabel = useDefaultView ? t('ragSourceMapV2.legendSizeDefault') : t('ragSourceMapV2.legendSizeUsage');
  const tarkistettavaCount = panelData.tarkistettava?.length || 0;

  return (
    <PanelColumn>
      <PanelHeader>
        <PanelTitleRow>
          <PanelTitle>{title}</PanelTitle>
          {switcher}
        </PanelTitleRow>
        <PanelAxisLabel>{t(`ragSourceMapV2.axis.${panelData.axisKey}`)}</PanelAxisLabel>
        <PanelCounter>
          {useDefaultView
            ? t('ragSourceMapV2.counterTotal', { total: panelData.counters.total })
            : t('ragSourceMapV2.counter', { total: panelData.counters.total, retrieved: panelData.counters.retrieved })}
        </PanelCounter>
        {tarkistettavaCount > 0 && (
          <TarkistettavaNote>{t('ragSourceMapV2.tarkistettavaNote', { count: tarkistettavaCount })}</TarkistettavaNote>
        )}
      </PanelHeader>

      <SvgWrap>
        <svg viewBox={viewBox} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
          {layout.clusters.map(cluster => {
            const color = panelData.clusterColors[cluster.key];
            return (
              <g key={cluster.key}>
                <circle
                  cx={cluster.center.x} cy={cluster.center.y} r={cluster.radius}
                  fill="none" stroke={color} strokeOpacity={cluster.empty ? 0.3 : 0.45}
                  strokeWidth={1.5} strokeDasharray={cluster.empty ? '3,4' : '5,4'}
                />
                <text
                  x={cluster.labelAnchor.x} y={cluster.labelAnchor.y}
                  fill={color} fillOpacity={cluster.empty ? 0.55 : 1}
                  textAnchor="middle" fontFamily={MONO_FONT}
                  fontSize={12} fontWeight={600} letterSpacing="0.06em"
                >
                  {t(`ragSourceMapV2.${labelNamespace}.${cluster.key}`).toUpperCase()}
                </text>
              </g>
            );
          })}

          {layout.nodes.map(node => {
            const color = panelData.clusterColors[node.cluster];
            const filled = useDefaultView || node.retrieved;
            const isHovered = hovered?.panelId === panelId && hovered.node.id === node.id;
            const isSelected = selected?.panelId === panelId && selected.node.id === node.id;
            const hitR = Math.max(node.r, HIT_RADIUS);
            return (
              <g key={node.id}>
                <circle
                  cx={node.x} cy={node.y} r={hitR} fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelect({ panelId, node })}
                  onMouseEnter={() => onHover({ panelId, node })}
                  onMouseLeave={() => onHover(null)}
                >
                  <title>{node.title}</title>
                </circle>
                <circle
                  cx={node.x} cy={node.y} r={node.r}
                  fill={filled ? color : 'none'} fillOpacity={filled ? 0.85 : 0}
                  stroke={color} strokeOpacity={isHovered ? 0.95 : (filled ? 0.7 : 0.6)}
                  strokeWidth={filled ? 1 : 1.5}
                  style={{ pointerEvents: 'none' }}
                />
                {isSelected && (
                  <circle
                    cx={node.x} cy={node.y} r={node.r + 4}
                    fill="none" stroke={color} strokeWidth={1} strokeOpacity={0.9}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </SvgWrap>

      <Legend>
        {panelData.clusterOrder.map(key => (
          <LegendRow key={key}>
            <LegendSwatch color={panelData.clusterColors[key]} />
            {t(`ragSourceMapV2.${labelNamespace}.${key}`)}
          </LegendRow>
        ))}
        <LegendRow style={{ marginTop: 4, opacity: 0.7 }}>
          <LegendSwatch color="rgba(255,255,255,0.3)" style={{ width: 4, height: 4 }} />
          {sizeLabel}
        </LegendRow>
      </Legend>
    </PanelColumn>
  );
}

function RagSourceMapV2Screen({ screenMode = false }) {
  useScreenNavigation({ screenMode });
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [kuutioDatasetId, setKuutioDatasetId] = useState(null);
  const [useDefaultView, setUseDefaultView] = useState(false);
  const [selected, setSelected] = useState(null); // { panelId, node }
  const [hovered, setHovered] = useState(null);

  useScreenStateUpdates('ragSourceMapV2', {
    sourceCount: (data?.perustietopohja.counters.total || 0) + (data?.kuutio.counters.total || 0),
  });

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.RAG_SOURCE_MAP_V2(instanceId, kuutioDatasetId));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        if (cancelled) return;
        setData(json);
        setStatus((json.perustietopohja.counters.total + json.kuutio.counters.total) > 0 ? 'ready' : 'empty');
        // Server may resolve a different default than what we asked for
        // (e.g. first load, or a datasetId that's no longer assigned).
        if (json.kuutio.datasetId !== kuutioDatasetId) setKuutioDatasetId(json.kuutio.datasetId);
        // Preserve the selected source only if it still exists in the fresh
        // corpus — a KUUTIO panel switch can drop it, PERUSTIETOPOHJA never does.
        setSelected(prev => {
          if (!prev) return prev;
          const panel = prev.panelId === 'kuutio' ? json.kuutio : json.perustietopohja;
          const stillExists = panel.usageLayout.nodes.some(n => n.id === prev.node.id);
          return stillExists ? prev : null;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('RagSourceMapV2Screen: failed to load source map', err);
          setStatus('error');
        }
      }
    };

    load();
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, kuutioDatasetId]);

  const switcher = useMemo(() => {
    if (!data || data.kuutio.availableDatasets.length <= 1) return null;
    return (
      <Switcher
        aria-label={t('ragSourceMapV2.switcherLabel')}
        value={kuutioDatasetId || ''}
        onChange={(e) => setKuutioDatasetId(e.target.value)}
      >
        {data.kuutio.availableDatasets.map(d => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </Switcher>
    );
  }, [data, kuutioDatasetId, t]);

  return (
    <Container>
      <TopBar>
        <Title>{t('ragSourceMapV2.title')}</Title>
        <DefaultViewToggle $active={useDefaultView} onClick={() => setUseDefaultView(v => !v)}>
          {t('ragSourceMapV2.defaultViewToggle')}
        </DefaultViewToggle>
      </TopBar>

      {status === 'loading' && <StatusMessage>{t('ragSourceMapV2.loading')}</StatusMessage>}
      {status === 'empty' && <StatusMessage>{t('ragSourceMapV2.empty')}</StatusMessage>}
      {status === 'error' && <StatusMessage>{t('ragSourceMapV2.error')}</StatusMessage>}

      {data && status === 'ready' && (
        <SplitCanvas>
          <Panel
            panelId="perustietopohja"
            panelData={data.perustietopohja}
            title={t('ragSourceMapV2.perustietopohja')}
            labelNamespace="teema"
            useDefaultView={useDefaultView}
            selected={selected} hovered={hovered}
            onSelect={setSelected} onHover={setHovered}
            t={t}
          />
          <Panel
            panelId="kuutio"
            panelData={data.kuutio}
            title={data.kuutio.datasetName || t('ragSourceMapV2.title')}
            labelNamespace="voices"
            useDefaultView={useDefaultView}
            switcher={switcher}
            selected={selected} hovered={hovered}
            onSelect={setSelected} onHover={setHovered}
            t={t}
          />
        </SplitCanvas>
      )}

      {selected && (() => {
        const panel = selected.panelId === 'kuutio' ? data.kuutio : data.perustietopohja;
        const labelNamespace = selected.panelId === 'kuutio' ? 'voices' : 'teema';
        const node = selected.node;
        const accent = panel.clusterColors[node.cluster];
        const meta = [node.publisher, node.publicationYear].filter(Boolean).join(' · ');
        return (
          <DetailPanel accent={accent}>
            <CloseButton onClick={() => setSelected(null)}>&times;</CloseButton>
            <DetailTitle>{node.title}</DetailTitle>
            <DetailType accent={accent}>{t(`ragSourceMapV2.${labelNamespace}.${node.cluster}`)}</DetailType>
            {!node.retrieved && !useDefaultView && <NotRetrievedBadge>{t('ragSourceMapV2.notRetrieved')}</NotRetrievedBadge>}

            {meta && <DetailMeta>{meta}</DetailMeta>}
            {node.description && <DetailDescription>{node.description}</DetailDescription>}

            {useDefaultView ? (
              <>
                <DetailTotal>{node.chunkCount}</DetailTotal>
                <DetailTotalLabel>{t('ragSourceMapV2.chunksLabel')}</DetailTotalLabel>
              </>
            ) : (
              <>
                <DetailTotal>{node.totalRetrievals}</DetailTotal>
                <DetailTotalLabel>{t('ragSourceMapV2.retrievals')}</DetailTotalLabel>
                <div>
                  {Object.entries(node.byContext || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([context, count]) => (
                      <ContextRow key={context}>
                        <span>{contextLabel(t, context)}</span>
                        <span>{count}</span>
                      </ContextRow>
                    ))}
                </div>
              </>
            )}
          </DetailPanel>
        );
      })()}
    </Container>
  );
}

export default RagSourceMapV2Screen;
